import { ReactNode, useCallback } from 'react';
import { toast, ToastOptions } from 'react-toastify';

const baseOptions: ToastOptions = {
  position: 'bottom-right',
  draggable: true,
  closeOnClick: false,
  autoClose: 4000,
  pauseOnHover: true,
  pauseOnFocusLoss: true,
  theme: 'light',
};

export const useNotifications = () => {
  const showSuccessMessage = useCallback((message: string | ReactNode, options?: ToastOptions, onOpen?: () => void) => {
    const opts = {
      ...baseOptions,
      ...options,
      autoClose: options?.delay ? 7000 : baseOptions.autoClose,
      onOpen,
    };
    toast.success(message, opts);
  }, []);

  const showErrorMessage = useCallback((message: string | ReactNode, options?: ToastOptions, onOpen?: () => void) => {
    toast.error(message, { ...baseOptions, ...options, onOpen });
  }, []);

  const showWarningMessage = useCallback(
    (message: string | ReactNode, options?: ToastOptions, onOpen?: () => void): void => {
      toast.warning(message, { ...baseOptions, ...options, onOpen });
    },
    [],
  );

  const showInfoMessage = useCallback(
    (message: string | ReactNode, options?: ToastOptions, onOpen?: () => void): void => {
      const opts = {
        ...baseOptions,
        ...options,
        autoClose: options?.delay ? 7000 : baseOptions.autoClose,
        onOpen,
      };
      toast.info(message, opts);
    },
    [],
  );

  const dismiss = useCallback((): void => {
    toast.dismiss();
  }, []);

  // Feature specific notifications

  // PRUNING
  const showPruningScheduledMessage = (name: string) =>
    showSuccessMessage(
      <div>
        Pruning for <strong>{name}</strong> was scheduled
      </div>,
    );

  const showPruningRemovedMessage = (name: string) =>
    showSuccessMessage(
      <div>
        Pruning for <strong>{name}</strong> was removed
      </div>,
    );

  // BACKUPS
  const showBackupScheduledMessage = (name: string) =>
    showSuccessMessage(
      <div>
        Backup for <strong>{name}</strong> was scheduled
      </div>,
    );

  const showBackupRemovedMessage = (name: string) =>
    showSuccessMessage(
      <div>
        Backup schedule for <strong>{name}</strong> was removed
      </div>,
    );

  // ID DUMPS
  const showIdentityDumpScheduledMessage = (name: string) =>
    showSuccessMessage(
      <div>
        Identity dumps for <strong>{name}</strong> were scheduled
      </div>,
    );

  const showIdentityDumpScheduleRemovedMessage = (name: string) =>
    showSuccessMessage(
      <div>
        Identity dump schedule for <strong>{name}</strong> was removed
      </div>,
    );

  // PARTIES
  const showPartyCreatedMessage = (name: string) =>
    showSuccessMessage(
      <div>
        Party <strong>{name ?? ''}</strong> was created
      </div>,
    );

  // USERS
  const showUserCreatedMessage = (name: string) =>
    showSuccessMessage(
      <div>
        User <strong>{name}</strong> was created
      </div>,
    );

  const showRightsUpdatedMessage = (name: string) =>
    showSuccessMessage(
      <div>
        <strong>{name}</strong>`s rights were updated
      </div>,
    );

  const showRightsUnchangedMessage = () => showInfoMessage('Nothing to update — your rights are unchanged');

  const onSuccessUserRemove = (name: string) =>
    showSuccessMessage(
      <div>
        User <strong>{name}</strong> was removed
      </div>,
    );

  // FILES
  const showFileUploadedMessage = (names: string[], count: number) => {
    if (count > 1) {
      showSuccessMessage(
        <div>
          Files <strong>{names.join(', ')}</strong> were uploaded
        </div>,
      );
    } else {
      showSuccessMessage(
        <div>
          File <strong>{names[0]}</strong> was uploaded
        </div>,
      );
    }
  };

  const onSuccessFileRemove = (name: string): void =>
    showSuccessMessage(
      <div>
        File <strong>{name}</strong> was removed
      </div>,
    );

  const onSuccessUploadDarToParticipantMessage = (fileName: string, participantName: string): void =>
    showSuccessMessage(
      <div>
        File <strong>{fileName}</strong> was deployed to <strong>{participantName}</strong>
      </div>,
    );

  const onSuccessAddDarToParticipantMessage = (
    names: Array<string>,
    participantName: string,
    count: number,
    isRemote = false,
  ): void => {
    if (count > 1) {
      showSuccessMessage(
        <div>
          DAR files <strong>{names.join(', ')}</strong> were added to ${isRemote ? 'remote' : ''} participant{' '}
          <strong>{participantName}</strong>
        </div>,
      );
    } else {
      showSuccessMessage(
        <div>
          DAR file <strong>{names[0]}</strong> was added to ${isRemote ? 'remote' : ''} participant{' '}
          <strong>{participantName}</strong>
        </div>,
      );
    }
  };

  // DOMAINS
  const showDomainCreatedMessage = (name: string) =>
    showSuccessMessage(
      <div>
        Domain <strong>{name}</strong> was created
      </div>,
      {},
    );

  const onSuccessDomainUpdatedMessage = (name: string): void =>
    showSuccessMessage(
      <div>
        Domain <strong>{name}</strong> was updated
      </div>,
      {},
    );

  // REMOTE DOMAIN
  const onSuccessRemoteDomainCreatedMessage = (name: string): void =>
    showSuccessMessage(
      <div>
        Remote domain <strong>{name}</strong> was created
      </div>,
      {},
    );

  const onSuccessRemoteDomainUpdatedMessage = (name: string): void =>
    showSuccessMessage(
      <div>
        Remote domain <strong>{name}</strong> was updated
      </div>,
      {},
    );

  // PARTICIPANTS
  const onSuccessParticipantCreatedMessage = (name: string) =>
    showSuccessMessage(
      <div>
        Participant <strong>{name}</strong> was created
      </div>,
      {},
    );

  const onSuccessParticipantUpdatedMessage = (name: string) =>
    showSuccessMessage(
      <div>
        Participant <strong>{name}</strong> was updated
      </div>,
      {},
    );

  const onSuccessConnectParticipantToDomainMessage = (participantName: string, domainName: string): void =>
    showSuccessMessage(
      <div>
        Participant <strong>{participantName}</strong> was connected to domain <strong>{domainName}</strong>
      </div>,
      {},
    );

  // REMOTE PARTICIPANTS
  const onSuccessRemoteParticipantCreatedMessage = (name: string): void =>
    showSuccessMessage(
      <div>
        Remote participant <strong>{name}</strong> was created
      </div>,
    );

  const onSuccessRemoteParticipantUpdatedMessage = (name: string): void =>
    showSuccessMessage(
      <div>
        Remote participant <strong>{name}</strong> was updated
      </div>,
      {},
    );

  const onSuccessConnectRemoteParticipantToDomainMessage = (participantName: string, domainName: string): void =>
    showSuccessMessage(
      <div>
        Remote participant <strong>{participantName}</strong> was connected to domain <strong>{domainName}</strong>
      </div>,
      {},
    );

  // APPLICATIONS
  const onSuccessApplicationCreated = (name: string) =>
    showSuccessMessage(
      <div>
        Application <strong>{name}</strong> was created
      </div>,
      {},
    );

  const onSuccessApplicationUpdated = (name: string) =>
    showSuccessMessage(
      <div>
        Application <strong>{name}</strong> was updated
      </div>,
      {},
    );

  // VALIDATORS
  const onSuccessCreateIdentitiesDump = (name: string) =>
    showSuccessMessage(
      <div>
        Validator participant identities saved as secret: <code>{name}</code>
      </div>,
    );

  const onSuccessValidatorCreateMessage = (name: string) =>
    showSuccessMessage(
      <div>
        Validator <strong>{name}</strong> was created
      </div>,
    );

  const onSuccessValidatorUpdateMessage = (name: string) =>
    showSuccessMessage(
      <div>
        Validator <strong>{name}</strong> was updated
      </div>,
      {},
    );

  const onSuccessValidatorWalletUserChangeMessage = (walletUser: string) =>
    showSuccessMessage(
      <div>
        Wallet user was changed to <strong>{walletUser}</strong>
      </div>,
    );

  const onSuccessValidatorRemove = (name: string) =>
    showSuccessMessage(
      <div>
        Validator <strong>{name}</strong> was removed
      </div>,
    );

  return {
    dismiss,
    showSuccessMessage,
    showErrorMessage,
    showWarningMessage,
    showInfoMessage,
    showPruningScheduledMessage,
    showPruningRemovedMessage,
    showBackupScheduledMessage,
    showBackupRemovedMessage,
    showIdentityDumpScheduledMessage,
    showIdentityDumpScheduleRemovedMessage,
    showPartyCreatedMessage,
    showUserCreatedMessage,
    onSuccessUserRemove,
    showRightsUpdatedMessage,
    showRightsUnchangedMessage,
    showFileUploadedMessage,
    onSuccessFileRemove,
    onSuccessUploadDarToParticipantMessage,
    onSuccessAddDarToParticipantMessage,
    showDomainCreatedMessage,
    onSuccessDomainUpdatedMessage,
    onSuccessValidatorCreateMessage,
    onSuccessValidatorUpdateMessage,
    onSuccessValidatorWalletUserChangeMessage,
    onSuccessValidatorRemove,
    onSuccessCreateIdentitiesDump,
    onSuccessApplicationCreated,
    onSuccessApplicationUpdated,
    onSuccessConnectParticipantToDomainMessage,
    onSuccessParticipantCreatedMessage,
    onSuccessParticipantUpdatedMessage,
    onSuccessRemoteParticipantCreatedMessage,
    onSuccessRemoteParticipantUpdatedMessage,
    onSuccessConnectRemoteParticipantToDomainMessage,
    onSuccessRemoteDomainCreatedMessage,
    onSuccessRemoteDomainUpdatedMessage,
  };
};
